import { Layers, Plus, Rows3, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaAmountInput, ColocaAmountInputControlled } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { isDecimalString } from "@/shared/utils/decimal";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import { formatAmountText, formatRangeLines } from "../../utils/feeFormat";
import { computeFee, rangeForAmount, tierRanges } from "../../utils/feeMath";
import { candidatePrice, type PRICE_FORM_DEFAULTS } from "./feeFormSchema";
import { FeeRangeRuler } from "./FeeRangeRuler";

type PriceFields = typeof PRICE_FORM_DEFAULTS;

export interface FeeTiersEditorProps {
  /** The currency every amount here is in, or the "operation currency" fallback for an any-currency rule. */
  currencyLabel: string;
  exampleAmount: string;
  /** The base percentage / fixed / min / max fields, rendered between the price type and the ranges. */
  children: ReactNode;
}

/**
 * Price type + ranges. A range is a band of the amount of ONE operation (never accumulated volume): the
 * engine picks the band whose start is the greatest one `<=` the gross, and its rate replaces the base pair
 * on the whole amount.
 */
export function FeeTiersEditor({ currencyLabel, exampleAmount, children }: FeeTiersEditorProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const form = useFormContext<PriceFields>();
  const { fields, append, remove, replace } = useFieldArray({ control: form.control, name: "tiers" });
  const [testAmount, setTestAmount] = useState(exampleAmount);

  const values = useWatch({ control: form.control }) as PriceFields;
  const useTiers = values.useTiers ?? false;
  const tiers = values.tiers ?? [];

  const sortAscending = () => {
    const sorted = [...tiers].sort((a, b) => (Number(a.threshold) || 0) - (Number(b.threshold) || 0));
    // `replace` remounts every row and drops focus — only pay that when the order actually changed.
    if (sorted.some((tier, index) => tier !== tiers[index])) replace(sorted);
  };

  const candidate = { ...candidatePrice(values), currency: currencyLabel };
  const ranges = tierRanges(candidate);
  const lines = formatRangeLines(candidate, activeLanguage, {
    ...labels.formula,
    anyCurrency: currencyLabel,
  });
  const probe = isDecimalString(testAmount) ? testAmount : null;
  const activeRange = probe ? rangeForAmount(ranges, probe) : null;
  const activeIndex = activeRange ? ranges.indexOf(activeRange) : null;
  const probeFee = probe ? computeFee(candidate, probe) : null;

  const sortedThresholds = tiers
    .map((tier) => tier.threshold)
    .filter((threshold) => isDecimalString(threshold))
    .sort((a, b) => Number(a) - Number(b));
  const lowest = sortedThresholds[0];
  const nextThreshold = (threshold: string) =>
    sortedThresholds.find((value) => Number(value) > Number(threshold));

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ColocaLabel>{t("price_structure_label")}</ColocaLabel>
        <ColocaChoiceCards
          aria-label={t("price_structure_label")}
          columns={2}
          value={useTiers ? "RANGES" : "SINGLE"}
          onValueChange={(value) => {
            const next = value === "RANGES";
            form.setValue("useTiers", next, { shouldValidate: true, shouldDirty: true });
            if (next && tiers.length === 0) append({ threshold: "", fixed: "", percentage: "" });
          }}
          items={[
            {
              value: "SINGLE",
              label: t("price_single_label"),
              description: t("price_single_hint"),
              icon: <Rows3 className="size-5 text-muted-foreground" />,
            },
            {
              value: "RANGES",
              label: t("price_ranges_label"),
              description: t("price_ranges_hint"),
              icon: <Layers className="size-5 text-primary" />,
            },
          ]}
        />
      </div>

      {children}

      {useTiers && (
        <fieldset className="space-y-3 rounded-xl border border-border p-3">
          <legend className="px-1.5 text-xs font-medium">{t("tiers_legend")}</legend>

          <FeeRangeRuler lines={lines} activeIndex={activeIndex} />

          <div className="hidden grid-cols-[1fr_1fr_1fr_1fr_2.25rem] gap-2 pt-1 text-[11px] font-medium text-muted-foreground sm:grid">
            <span>{t("tiers_th_from")}</span>
            <span>{t("tiers_th_to")}</span>
            <span>{t("tiers_th_percentage")}</span>
            <span>{t("tiers_th_fixed")}</span>
            <span />
          </div>

          {lowest && Number(lowest) > 0 && (
            <p className="rounded-lg bg-muted/60 px-2.5 py-1.5 text-xs text-muted-foreground">
              {t("tiers_base_row", {
                amount: `${formatAmountText(lowest, activeLanguage)} ${currencyLabel}`,
              })}
            </p>
          )}

          <div className="flex flex-col gap-2">
            {fields.map((field, index) => {
              const threshold = tiers[index]?.threshold ?? "";
              const next = isDecimalString(threshold) ? nextThreshold(threshold) : undefined;
              return (
                <div
                  key={field.id}
                  className="grid grid-cols-2 items-start gap-2 sm:grid-cols-[1fr_1fr_1fr_1fr_2.25rem]"
                >
                  <ColocaAmountInput
                    name={`tiers.${index}.threshold`}
                    aria-label={t("tiers_th_from")}
                    suffix={currencyLabel}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                    onBlur={sortAscending}
                  />
                  <span className="flex h-9 items-center truncate rounded-md border border-dashed border-border px-2.5 text-sm text-muted-foreground tabular-nums">
                    {next
                      ? `${formatAmountText(next, activeLanguage)} ${currencyLabel}`
                      : t("tiers_and_above")}
                  </span>
                  <ColocaAmountInput
                    name={`tiers.${index}.percentage`}
                    aria-label={t("tiers_th_percentage")}
                    suffix="%"
                    maxFractionDigits={4}
                    placeholder="0"
                  />
                  <ColocaAmountInput
                    name={`tiers.${index}.fixed`}
                    aria-label={t("tiers_th_fixed")}
                    suffix={currencyLabel}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                  />
                  <ColocaIconButton
                    icon={X}
                    variant="ghost"
                    size="sm"
                    aria-label={t("tiers_remove_aria")}
                    className="mt-0.5 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(index)}
                  />
                </div>
              );
            })}
          </div>

          <ColocaButton
            type="button"
            variant="default"
            size="xs"
            icon={Plus}
            onClick={() => append({ threshold: "", fixed: "", percentage: "" })}
          >
            {t("tiers_add")}
          </ColocaButton>

          <p className="text-xs text-muted-foreground">{t("tiers_help")}</p>

          <div className="flex flex-col gap-2 rounded-lg bg-primary/5 p-3 sm:flex-row sm:items-center">
            <ColocaLabel htmlFor="fee-tier-probe" className="shrink-0 text-xs">
              {t("tiers_example_label")}
            </ColocaLabel>
            <ColocaAmountInputControlled
              id="fee-tier-probe"
              className="sm:w-48"
              value={testAmount}
              onValueChange={setTestAmount}
              suffix={currencyLabel}
              maxFractionDigits={8}
              groupThousands
            />
            {probe && activeIndex != null && probeFee && (
              <p className="text-xs" aria-live="polite">
                {t("tiers_example", {
                  amount: `${formatAmountText(probe, activeLanguage)} ${currencyLabel}`,
                  range: lines[activeIndex].label,
                  fee: `${formatAmountText(probeFee.total, activeLanguage)} ${currencyLabel}`,
                })}
              </p>
            )}
          </div>
        </fieldset>
      )}
    </div>
  );
}
